import { Box, Group, Loader, Paper, Radio, Stack, Text, Tooltip } from '@mantine/core'
import { modals } from '@mantine/modals'
import { IconAlertTriangle } from '@tabler/icons-react'
import { observer } from 'mobx-react-lite'
import { useEffect, useEffectEvent, useRef, useState } from 'react'

import { InputLabel } from '@/components/InputLabel'
import { RadioCard } from '@/components/RadioCard'
import { theme } from '@/configs/theme'
import { usePresentationStyles } from '@/hooks/usePresentationStyles'
import type { CMFormSection } from '@/screens/ContentModuleFormScreen/ContentModuleFormScreen'
import { useStepResources } from '@/screens/ContentModuleFormScreen/useStepResources'
import { isDisplayable } from '@/utils/contentAccess'
import { splitMediaSteps } from '@/utils/contentModuleForm'
import { formatContentType } from '@/utils/contentTypes'
import {
  canSelectMediaMode,
  CUSTOM_DELIVERY_MODE,
  defaultDeliveryModeName,
  deliveryModeBlurb,
  deliveryModeLabel,
  demotionForPrimary,
  isMediaModeName,
  MEDIA_BARRED_TOOLTIP,
  storedDeliveryMode,
} from '@/utils/deliveryModes'
import { hasStepFailed, primaryFailureRepair } from '@/utils/stepReadiness'

const CUSTOM_BLURB = 'A delivery style written for this module before delivery modes existed. Kept as it was.'

const MEDIA_PENDING_TOOLTIP = 'Checking whether your primary file plays in the app.'

const MEDIA_UNKNOWN_TOOLTIP =
  "We couldn't read your primary file, so we can't tell whether it plays in the app. Reload the page to try again."

export const DeliveryModeSection = observer(function DeliveryModeSection(props: CMFormSection) {
  const stylesQuery = usePresentationStyles()

  // The module form is uncontrolled, so a setFieldValue here would not come back as a
  // render. The selection is held locally and written through, and re-seeded when the
  // saved module arrives or changes underneath.
  const saved = props.cmQuery.data
  // The form wins over the saved module wherever it carries a choice: it is what Save will
  // send. Only the legacy `is_media_mode` fallback has to come off the server, since no
  // form field records it.
  const readMode = () =>
    storedDeliveryMode({
      presentationStyleName: props.moduleForm.getValues().deliveryStyleName,
      presentationStyle: props.moduleForm.getValues().deliveryStyle,
      isMediaMode: props.cmQuery.data?.isMediaMode,
    })
  const [value, setValue] = useState(readMode)

  const { primary: primaryStep, supplementary: otherSteps } = splitMediaSteps(
    props.stepsQuery.data,
    saved?.primaryStepId,
  )
  // Which repair the alert below offers — decision 7 only allows Delete when it is alone.
  const hasOtherFiles = otherSteps.length > 0
  const { sourceFileResource, stepResourcesSettled } = useStepResources(primaryStep?.stepId)

  // Her rule, decision 5: Media needs a primary the app can actually play. That is the
  // same test the primary's own access resolves on, so it covers her file-type list and
  // her "or they have selected Information Only" clause in one.
  // A file that failed to process cannot lead a Media module, whatever its format allows —
  // including a failed transcode, which this used to miss and which is the only failure a
  // video has.
  const primaryFailed = !!primaryStep && hasStepFailed(primaryStep)
  const primaryFacts = {
    type: sourceFileResource?.type,
    showInAppSupported: sourceFileResource?.showInAppSupported,
    // This file already leads the module, and nothing here will correct it — decision 27
    // keeps a deliberate hide rather than un-hiding it on an incidental save. So a hidden
    // lead file bars Media and the mode demotes with the notice, which is the visible half
    // of that trade.
    showInApp: sourceFileResource?.showInApp,
    hasError: primaryFailed,
  }
  const mediaAllowed = canSelectMediaMode(primaryFacts)
  // Media is barred for two different reasons now, and they need different sentences. The
  // format having no viewer is a fact about the file type; a hidden lead file is a choice
  // the expert made, on a format that plays perfectly well (decision 36).
  const primaryHidden =
    isDisplayable({ type: sourceFileResource?.type, showInAppSupported: sourceFileResource?.showInAppSupported }) &&
    sourceFileResource?.showInApp === false
  const primaryAccessSettled = !!primaryStep && stepResourcesSettled && !!sourceFileResource
  // Settled with nothing to judge: the resources fetch failed, or the primary has no
  // source file yet. The facts never arrive, so this gates the card rather than the
  // demotion — withholding Media is safe, demoting a module on a network blip is not.
  const primaryAccessPending = !!primaryStep && !stepResourcesSettled
  const primaryAccessUnknown = !!primaryStep && stepResourcesSettled && !sourceFileResource
  const [demotedTo, setDemotedTo] = useState<string | null>(null)

  const reseedFromSaved = useEffectEvent(() => {
    if (!saved) return
    setValue(readMode())
  })
  useEffect(() => {
    reseedFromSaved()
  }, [saved?.presentationStyleName, saved?.presentationStyle, saved?.isMediaMode])

  // The module form is uncontrolled, so `form.setValues` — restoring a cached draft, above
  // all — reaches every other input by remounting it on `form.key`, and cannot reach this
  // state at all. Without reconciling, the picker keeps showing the saved mode while the
  // form holds the restored one, and Save sends the mode that was never on screen.
  const formStyleName = props.moduleForm.getValues().deliveryStyleName?.trim() ?? ''
  const adoptFormStyle = useEffectEvent((name: string) => {
    if (name && name !== value) setValue(name)
  })
  useEffect(() => {
    adoptFormStyle(formStyleName)
  }, [formStyleName])

  // "Media is selected by default. If disallowed based on file type, then choose Discovery
  // by default." Only ever fires on a module with no recorded choice and no legacy prose,
  // so it can't overwrite what an expert picked or wrote. Dirty state is restored so
  // opening a module doesn't raise an unsaved-changes prompt on its own.
  const seedDefaultMode = useEffectEvent((styleNames: string[]) => {
    const name = defaultDeliveryModeName(primaryFacts, styleNames)
    if (!name) return

    const wasDirty = props.moduleForm.isDirty()
    applyStyle(name)
    if (!wasDirty) props.moduleForm.resetDirty()
  })
  useEffect(() => {
    if (value !== '') return
    if (!stylesQuery.data?.length || !primaryAccessSettled) return

    seedDefaultMode(stylesQuery.data.map(s => s.name))
  }, [value, stylesQuery.data, primaryAccessSettled, mediaAllowed])

  // `deliveryStyle` is this section's field now, and it is still a submit gate. Without a
  // ref registered here "Scroll to Error" had nothing to scroll to.
  const deliveryStyleRef = useRef<HTMLDivElement>(null)
  const { setFieldRefs } = props
  useEffect(() => {
    setFieldRefs?.({ deliveryStyle: deliveryStyleRef } as Record<string, React.RefObject<HTMLElement>>)
  }, [setFieldRefs])

  // C2-2598 recorded the chosen mode and deliberately did not backfill, so a legacy media
  // module carries only `is_media_mode`. The radio reads that and shows Media, but the form
  // field stays empty and re-picking the selected card is a no-op — leaving the module
  // unable to record the mode it is already in, and the read-only view calling it Custom.
  // Written through once, non-dirty like the default seed above: it is what the module
  // already is, not a change to it.
  const recordLegacyMode = useEffectEvent((name: string) => {
    if (props.moduleForm.getValues().deliveryStyleName?.trim()) return

    const wasDirty = props.moduleForm.isDirty()
    const wasTouched = props.moduleForm.isTouched()
    applyStyle(name)
    if (!wasDirty) props.moduleForm.resetDirty()
    if (!wasTouched) props.moduleForm.resetTouched()
  })
  useEffect(() => {
    if (!value || value === CUSTOM_DELIVERY_MODE) return
    if (!stylesQuery.data?.some(style => style.name === value)) return

    recordLegacyMode(value)
  }, [value, stylesQuery.data])

  // The notice explains a demotion that happened; it must not outlive the state that
  // caused it, or a later primary that also can't carry Media reads as a fresh demotion.
  useEffect(() => {
    if (mediaAllowed) setDemotedTo(null)
  }, [mediaAllowed])

  // This screen is one route shared between modules, so it isn't remounted on a switch.
  useEffect(() => {
    setDemotedTo(null)
  }, [props.taskId])

  function applyStyle(name: string) {
    const style = stylesQuery.data?.find(s => s.name === name)
    if (!style) return
    props.moduleForm.setFieldValue('deliveryStyleName', style.name)
    props.moduleForm.setFieldValue('deliveryStyle', style.description)
    props.moduleForm.clearFieldError('deliveryStyle')
    setValue(style.name)
  }

  function commitStyle(next: string) {
    // Their own choice supersedes the explanation of ours.
    setDemotedTo(null)
    applyStyle(next)
  }

  // The mockup's `reconcileMode`. A primary that can't carry Media demotes the mode
  // rather than leaving the module recorded as something the app will try to play and
  // can't. The swap dialog says this is coming; the notice below says it happened.
  //
  // Deliberately leaves the form dirty: this is a real change to the module and the
  // expert needs to save it, unlike the default seed above which only pre-fills. It does
  // not leave it *touched*, because the guard in `useContentModuleForm` keys the cached
  // draft and the leave blocker on dirty and touched together, and neither belongs to a
  // change the expert never made. Their first real edit arms both.
  const demoteForPrimary = useEffectEvent((mode: string, styles: NonNullable<typeof stylesQuery.data>) => {
    const fallback = demotionForPrimary(
      mode,
      primaryFacts,
      styles.map(s => s.name),
    )
    if (!fallback) return

    const wasTouched = props.moduleForm.isTouched()
    applyStyle(fallback)
    if (!wasTouched) props.moduleForm.resetTouched()
    const style = styles.find(s => s.name === fallback)
    setDemotedTo(deliveryModeLabel(fallback, style?.displayName || fallback))
  })
  useEffect(() => {
    if (!primaryAccessSettled) return
    if (!stylesQuery.data?.length) return

    demoteForPrimary(value, stylesQuery.data)
  }, [primaryAccessSettled, mediaAllowed, value, stylesQuery.data])

  function handleChange(next: string) {
    if (next === value) return
    if (isMediaModeName(next) && !mediaAllowed) return

    if (value === CUSTOM_DELIVERY_MODE) {
      modals.openConfirmModal({
        title: 'Replace your custom delivery style?',
        centered: true,
        children: (
          <Text size="sm">
            This module uses a delivery style written by hand. Choosing a mode replaces it, and the original wording
            can&rsquo;t be recovered.
          </Text>
        ),
        labels: { confirm: 'Replace it', cancel: 'Keep what I have' },
        confirmProps: { color: 'red' },
        onConfirm: () => commitStyle(next),
      })
      return
    }

    commitStyle(next)
  }

  if (stylesQuery.isPending) {
    return (
      <Stack align="center" py="xl">
        <Loader size="sm" />
      </Stack>
    )
  }

  if (stylesQuery.isError || !stylesQuery.data?.length) {
    return (
      <Text c="dimmed" size="sm">
        Delivery modes couldn&rsquo;t be loaded. Reload the page to try again.
      </Text>
    )
  }

  // A swap saves the correction with itself, so past tense there. Reaching this by opening
  // a module whose stored mode already disagreed does not, so future tense.
  const demotionNote =
    demotedTo &&
    (isMediaModeName(storedDeliveryMode(saved ?? {}))
      ? ` Delivery will change to ${demotedTo} when you save.`
      : ` Delivery has changed to ${demotedTo}.`)

  return (
    <Radio.Group
      ref={deliveryStyleRef}
      tabIndex={-1}
      value={value}
      onChange={handleChange}
      error={props.moduleForm.errors.deliveryStyle}>
      <Stack gap="sm" mt="xs">
        {stylesQuery.data.map(style => {
          // Her call: "I don't care if we hide media or gray it out." Grayed, so the mode
          // stays visible and the tooltip can say why it isn't available.
          // Barred unless the facts are in and say yes — the same test `handleChange`
          // enforces. Anything looser leaves a card that looks available and does nothing.
          // Never the current selection, though: greying that out while the facts load
          // leaves the group showing nothing selected. If it turns out it can't carry
          // Media, the demotion below moves the selection and then this bars it.
          const barred = isMediaModeName(style.name) && style.name !== value && !(primaryAccessSettled && mediaAllowed)

          return (
            <Tooltip
              key={style.presentationStyleId}
              label={
                primaryFailed
                  ? `Your Primary File failed to process. ${primaryFailureRepair(hasOtherFiles)}`
                  : primaryAccessPending
                    ? MEDIA_PENDING_TOOLTIP
                    : primaryAccessUnknown
                      ? MEDIA_UNKNOWN_TOOLTIP
                      : MEDIA_BARRED_TOOLTIP
              }
              disabled={!barred}
              withArrow>
              {/* The disabled card takes no pointer events, so the tooltip hangs off a
                  wrapper that still receives hover. */}
              <Box>
                <RadioCard
                  value={style.name}
                  label={deliveryModeLabel(style.name, style.displayName || style.name)}
                  description={deliveryModeBlurb(style.name)}
                  alwaysShowDescription
                  disabled={barred}
                />
              </Box>
            </Tooltip>
          )
        })}

        {value === CUSTOM_DELIVERY_MODE && (
          <RadioCard value={CUSTOM_DELIVERY_MODE} label="Custom" description={CUSTOM_BLURB} alwaysShowDescription />
        )}
      </Stack>

      {/* A failed primary is an alert, not a dimmed line, and it names the two repairs it
          has: Delete is locked for it (decision 7) and pointing there hands the lead slot to
          a supplementary file. The two cases below are notes rather than alerts: one is a
          property of the format, the other a setting on the file. Not gated on the resources
          fetch, since a failed file may have no source resource for it to land. */}
      {primaryFailed ? (
        <Group gap={theme.rem(6)} mt="sm" wrap="nowrap" align="flex-start">
          <IconAlertTriangle size={16} aria-hidden style={{ flexShrink: 0, marginTop: theme.rem(2) }} />
          <Text size="sm" c="yellow.8">
            Processing failed for {primaryStep?.name || 'your primary file'}. {primaryFailureRepair(hasOtherFiles)}
            {demotionNote}
          </Text>
        </Group>
      ) : (
        primaryAccessSettled &&
        !mediaAllowed && (
          <Text size="sm" c="dimmed" mt="sm">
            {primaryHidden
              ? `Your primary file is set to Information Only, so the app won't show it. ${primaryFailureRepair(hasOtherFiles)}`
              : `Your primary file (${formatContentType(sourceFileResource?.type)}) has no in-app viewer.`}
            {demotionNote}
          </Text>
        )
      )}

      {value === CUSTOM_DELIVERY_MODE && (
        <Stack gap={theme.rem(6)} mt="md">
          <InputLabel label="Custom delivery style" withSpacer={false} />
          <Paper withBorder radius="md" p="sm" bg="gray.0">
            <Text size="sm" c="dimmed" style={{ whiteSpace: 'pre-wrap' }}>
              {props.moduleForm.getValues().deliveryStyle}
            </Text>
          </Paper>
        </Stack>
      )}
    </Radio.Group>
  )
})
